import { HistoryOutlined } from "@ant-design/icons"
import { Card, Flex, Typography } from "antd"
import React from "react"

import type { DocumentProposalVersion } from "../types/document.types.ts"
import { DocumentVersionRow } from "./DocumentVersionRow.tsx"

const { Title } = Typography

interface DocumentVersionHistoryListProps {
  versionHistory: DocumentProposalVersion[]
  onEditVersion: (id: string) => void
  onViewVersion: (id: string) => void
}

/**
 * The full "Version 3 — Latest / Version 2 — Read-only / Version 1 —
 * Read-only" list the product owner's mockup showed. Built from a single
 * document's own `version_history` (already fetched by the edit page that
 * mounts this — no extra per-entry request), each entry now correctly
 * carrying its own `is_latest_version` (a prior backend pass left this off
 * `version_history` specifically, even though the top-level document's own
 * flag was already reliable — see `document.types.ts`).
 *
 * Newest first, matching the mockup. Every entry gets Edit or View per its
 * own `is_latest_version` — never assume there is exactly one Latest row in
 * a given array; render what the data actually says.
 */
export function DocumentVersionHistoryList({
  versionHistory,
  onEditVersion,
  onViewVersion
}: DocumentVersionHistoryListProps) {
  if (!versionHistory.length) return null

  const sorted = [...versionHistory].sort(
    (a, b) => b.version_number - a.version_number
  )

  return (
    <Card size="small" data-testid="document-version-history">
      <Flex align="center" gap="small" className="mb-3">
        <HistoryOutlined />
        <Title level={5} className="!mb-0">
          Version History
        </Title>
      </Flex>
      <Flex vertical gap="small">
        {sorted.map((entry) => (
          <DocumentVersionRow
            key={entry.id}
            versionNumber={entry.version_number}
            isLatest={entry.is_latest_version}
            onEdit={() => onEditVersion(entry.id)}
            onView={() => onViewVersion(entry.id)}
          />
        ))}
      </Flex>
    </Card>
  )
}
